import { Controller } from "@hotwired/stimulus"

// Up and down through a ladder of sensible quantities.
//
// NOT A FIXED INCREMENT. A number field stepping by 0.5 gets you to 1.75
// servings, which nobody means, and stepping by 1 makes half a banana
// unreachable. The ladder is the vocabulary people actually use — quarters
// and thirds below one, halves to three, then whole numbers — so every stop
// is a quantity somebody would say out loud.
//
// The ladder here is only for MOVING between values. How a value READS is
// the server's job: FractionalQuantities already knows that "3⅜ onions" is
// nonsense while "¼ tsp" is not, and that fractions stop above four whole
// things. Duplicating any of that in JavaScript would give the planner and
// the recipe page two different opinions about the same banana.
//
// Submits on a debounce rather than on every press, so holding the button
// through five steps is one request rather than five.
export default class extends Controller {
  static targets = ["input", "display", "unit", "submitter"]
  static values = {
    ladder: { type: Array, default: [] },
    min: { type: Number, default: 0 },
    delay: { type: Number, default: 400 },
    // WHETHER A STEP SAVES. On the planner and Today a stepper edits a meal
    // that already exists, so each step submits. In the picker it fills in a
    // quantity on a form you have not sent yet — stepping to 5 there used to
    // add the food five times on the way.
    //
    // Declared rather than sniffed: the first attempt asked whether the
    // element was a <form>, which is true in BOTH cases, because the picker
    // puts this controller on the add form itself.
    submits: { type: Boolean, default: false },
    // BOTH SPELLINGS OF THE UNIT, because only the server knows the words.
    // "serve"/"serves" on the planner, "serving"/"servings" on Today, and a
    // food's unit does not inflect at all.
    //
    // Given both, the label follows the number immediately. Without them it
    // stays put and reads "1 serves" until the response lands — which, at a
    // 400ms debounce, is long enough to see.
    unitOne: String,
    unitMany: String
  }

  connect() {
    this.timer = null
    // A value that arrived off-ladder was typed by somebody (or imported),
    // so it is exact and must read back as itself. See #showTyped.
    this.typed = this.hasInputTarget && !this.onLadder(this.current)
    if (this.typed) this.showTyped(this.current)
  }

  // Whether a value is one the arrows could have produced. Anything else was
  // entered deliberately.
  onLadder(value) {
    const rungs = this.ladderValue
    return !rungs.length || rungs.some((rung) => Math.abs(rung - value) < 1e-9)
  }

  // TYPED, so shown verbatim. FractionalQuantities rounds for display — 1.6
  // reads as "1½", 0.33 as "¼" — which is right for an imported recipe
  // saying "0.37 onions" and wrong for a number somebody just entered. It
  // cannot tell the two apart; this can, because it saw the typing.
  //
  // Only the DISPLAY differs. The value stored and submitted is untouched
  // either way, which is why the rounding was safe to begin with.
  showTyped(value) {
    if (!this.hasDisplayTarget) return

    const text = String(parseFloat(value.toFixed(3)))
    // Cheap, and avoids disturbing a selection for no reason.
    if (this.displayTarget.textContent.trim() === text) return

    this.displayTarget.textContent = text
  }

  // Editing the number by hand. The field is the authority from here.
  // TYPING UPDATES AS YOU TYPE. Bound to input, not blur: waiting for the
  // focus to leave meant the macros beside the field sat at the old amount
  // while you looked at the new one, which reads as them being broken.
  //
  // A part-typed value is not an error. "1." and "" happen on the way to
  // "1.5", so an unreadable field is simply left alone — nothing is written
  // and nothing is repainted — and #settle puts it right on blur.
  edit(event) {
    const value = this.parse(event.target.textContent)
    if (value === null) return

    this.typed = !this.onLadder(value)
    this.inputTarget.value = parseFloat(value.toFixed(3))

    // THE DISPLAY IS NOT REPAINTED WHILE YOU TYPE. It is the element being
    // typed into, and writing to textContent replaces the text node — which
    // drops the caret back to the start, so "165" came out as "561".
    //
    // What you typed is already what it says; #settle renders it properly
    // once the focus leaves.

    this.reflectUnit(value)
    this.reflectDefault(value)
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    if (this.submitsValue) this.schedule()
  }

  // WHAT THE FIELD SHOWS ONCE YOU LEAVE IT. Typing is left alone as it
  // happens — repainting mid-keystroke would fight the caret — so this is
  // where a half-typed or unreadable value is put back to the last good one,
  // and where a typed value gets its final rendering.
  settle() {
    const value = this.parse(this.displayTarget.textContent)
    if (value === null) {
      this.restore()
      return
    }

    this.typed = !this.onLadder(value)
    this.restore()
  }

  // Enter commits rather than inserting a line break.
  commit(event) {
    event.preventDefault()
    event.target.blur()
  }

  // Typing replaces the amount; it is never appended to.
  selectAll() {
    const range = document.createRange()
    range.selectNodeContents(this.displayTarget)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  }

  // READS WHAT PEOPLE WRITE, including the fractions we render. Somebody
  // editing "1½" is as likely to leave the glyph and change the 1 as to
  // retype the lot, and rejecting that would look broken.
  parse(text) {
    const glyphs = { "¼": 0.25, "½": 0.5, "¾": 0.75, "⅓": 1 / 3, "⅔": 2 / 3 }
    const trimmed = text.trim()
    if (!trimmed) return null

    const match = trimmed.match(/^(-?[\d.]*)\s*([¼½¾⅓⅔])?$/)
    if (!match) return null

    const whole = match[1] ? parseFloat(match[1]) : 0
    const part = match[2] ? glyphs[match[2]] : 0
    if (!Number.isFinite(whole)) return null

    const value = whole + part
    return value >= 0 ? value : null
  }

  restore() {
    const value = this.current
    if (this.typed) {
      this.showTyped(value)
    } else {
      this.showOptimistically(value)
    }
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
  }

  up() {
    this.moveTo(this.next(1))
  }

  down() {
    this.moveTo(this.next(-1))
  }

  // The next rung in a direction, or the current value if there is none.
  //
  // Finds where we are by nearest rung rather than by exact match: a value
  // typed by hand, or one stored before the ladder changed, still steps to
  // somewhere sensible instead of jumping to the bottom.
  next(direction) {
    const rungs = this.ladderValue
    if (!rungs.length) return this.current + direction

    const now = this.current
    if (direction > 0) {
      // PAST THE TOP RUNG IT KEEPS CLIMBING. The ladder ends at 30, which is
      // fine for servings and useless for grams — 250 g simply would not go
      // up. Beyond the top it steps by the ladder's own last interval, so a
      // gram field keeps moving in sensible jumps instead of stopping dead.
      return rungs.find((rung) => rung > now + 1e-9) ?? now + this.topInterval
    }

    // ABOVE THE TOP RUNG, step down by the same interval — dropping to the
    // ladder's last rung instead would take 250 g to 30 in one press.
    const top = rungs[rungs.length - 1]
    if (now > top + 1e-9) return Math.max(now - this.topInterval, top)

    const below = rungs.filter((rung) => rung < now - 1e-9)
    return below.length ? below[below.length - 1] : now
  }

  // The gap between the ladder's last two rungs — how far a step goes once
  // the rungs run out.
  get topInterval() {
    const rungs = this.ladderValue
    if (rungs.length < 2) return 1

    return rungs[rungs.length - 1] - rungs[rungs.length - 2]
  }

  get current() {
    const value = parseFloat(this.inputTarget.value)
    return Number.isFinite(value) ? value : 0
  }

  moveTo(value) {
    const clamped = Math.max(value, this.minValue)
    if (clamped === this.current) return

    // Trailing zeros off, so the field holds "1.5" and "2" rather than
    // "1.50" — it is submitted as text and read back as a decimal.
    this.inputTarget.value = parseFloat(clamped.toFixed(3))
    this.typed = false
    this.showOptimistically(clamped)
    this.reflectUnit(clamped)
    this.reflectDefault(clamped)

    // ANYTHING WATCHING THE VALUE hears about it. The picker's calorie
    // figure reads the hidden field, and a hidden field set by script
    // fires no event of its own — so without this the number beside the
    // stepper would sit still while the stepper moved.
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))

    if (this.submitsValue) this.schedule()
  }

  // THE FIGURE MOVES NOW, the server confirms it in a moment.
  //
  // Waiting for the round trip would leave the number sitting at its old
  // value for as long as the debounce — pressing + four times in a row
  // would look like nothing was happening.
  //
  // Only the shapes JavaScript can be trusted with: halves and quarters,
  // which are unambiguous. Anything else shows the decimal until the server
  // answers with the properly rendered fraction, because FractionalQuantities
  // knows things this cannot — that a third of a teaspoon is real and a
  // third of an onion is not.
  showOptimistically(value) {
    if (!this.hasDisplayTarget) return

    const whole = Math.floor(value)
    const part = value - whole
    // THIRDS TOO, since the ladder can land on them. Without 0.33 and 0.67
    // here a step onto a third fell through to the decimal and the field
    // read "0.67" until the server answered — the one rung where the
    // optimistic display contradicted the rendered one.
    //
    // The server still decides what a third READS as: it shows "⅓ tsp" and
    // "¼ apple" from the same 0.33, because a third of a teaspoon is real
    // and a third of an apple is not. This only has to be right for the
    // moment before that answer arrives.
    const glyph = {
      0.25: "¼", 0.33: "⅓", 0.5: "½", 0.67: "⅔", 0.75: "¾"
    }[Number(part.toFixed(2))]

    if (part === 0) {
      this.displayTarget.textContent = String(whole)
    } else if (glyph) {
      this.displayTarget.textContent = whole === 0 ? glyph : `${whole}${glyph}`
    } else {
      this.displayTarget.textContent = String(parseFloat(value.toFixed(2)))
    }
  }

  // "1 serve", not "1 serves". Only when the server gave us both words —
  // otherwise the unit is a food's own ("medium", "g"), which does not
  // inflect, and the safest thing is to leave it alone.
  reflectUnit(value) {
    if (!this.hasUnitTarget || !this.hasUnitOneValue || !this.hasUnitManyValue) return

    this.unitTarget.textContent = value === 1 ? this.unitOneValue : this.unitManyValue
  }

  // ONE SERVING HIDES ITSELF; anything else stays put.
  //
  // The class is decided when the card renders, and a stepper change no
  // longer re-renders the card — only its figures — so without this a meal
  // stepped from 1 to 2 kept the class that hides it, and vanished the
  // moment the pointer left.
  //
  // The server still writes the class on the next full render; this keeps
  // the page honest in between.
  reflectDefault(value) {
    const panel = this.element.closest(".meal-servings")
    if (!panel) return

    panel.classList.toggle("is-default", value === 1)
  }

  schedule() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.submit(), this.delayValue)
  }

  submit() {
    this.timer = null

    // requestSubmit ONLY. The old fallback called form.submit(), which does
    // not fire a submit event — so Turbo never sees it, the browser posts
    // the form itself, and the response is a full page load. That is a
    // silent downgrade: the save works, the page just reloads.
    //
    // Every browser this app supports has requestSubmit; a form that somehow
    // lacks it should do nothing rather than blow the page away mid-edit.
    if (typeof this.element.requestSubmit !== "function") return

    // SUBMITTED THROUGH A REAL BUTTON, which is the whole point of this.
    //
    // Rails renders method: :patch as method="post" plus a hidden _method
    // field, and Rails-UJS/Turbo only rewrite the verb when a SUBMITTER
    // carries it. requestSubmit() with no submitter dropped it, so a
    // stepper's save arrived as POST /logged-meals/109 — a route that does
    // not exist. The 404 came back as HTML, Turbo could not apply it, and
    // fell back to loading the page: the save silently lost, the page
    // reloaded, and nothing errored anywhere.
    //
    // The button lives in the form and is hidden; passing it to
    // requestSubmit is what makes this an ordinary submit.
    this.element.requestSubmit(this.submitterTarget || undefined)
  }
}
